import { httpsCallable } from 'firebase/functions';
import { functions } from '../firebase';
import { Coupon, User } from '../types';

export interface ValidationResult {
  isValid: boolean;
  message: string;
  coupon?: Coupon;
  errorCode?: string;
}

export interface ProcessResult {
  success: boolean;
  cashbackEarned?: number;
  newBalance?: number;
  pointsEarned?: number;
  newPointsBalance?: number;
  transactionId?: string;
  message: string;
}

export interface ValidationStep {
  step: string;
  status: 'pending' | 'completed' | 'failed';
  message?: string;
}

/**
 * Cleans and formats coupon code - now preserves dashes
 */
export const cleanCouponCode = (code: string): string => {
  let cleaned = code.trim();

  // If it's a URL or contains slashes, just take the very last part (the code itself)
  if (cleaned.includes('/')) {
    const parts = cleaned.split('/');
    cleaned = parts[parts.length - 1];
  }

  return cleaned.toUpperCase().replace(/[^A-Z0-9-]/g, ''); // Keep dashes
};

/**
 * Client-side coupon validation service
 */
export class CouponValidationService {

  /**
   * Validates coupon format and checks basic rules
   */
  private validateCouponFormat(couponCode: string): ValidationResult {
    const cleanCode = cleanCouponCode(couponCode);

    if (!cleanCode) {
      return {
        isValid: false,
        message: 'Please enter a coupon code',
        errorCode: 'EMPTY_CODE'
      };
    }

    // Updated pattern to support dashes: PNT-R3XH0SC2 format
    const couponPattern = /^[A-Z0-9-]{6,20}$/; // 6-20 alphanumeric characters with dashes
    if (!couponPattern.test(cleanCode)) {
      return {
        isValid: false,
        message: 'Invalid coupon format. Use 6-20 characters (letters, numbers, dashes).',
        errorCode: 'INVALID_FORMAT'
      };
    }

    return {
      isValid: true,
      message: 'Format valid'
    };
  }

  /**
   * Checks if coupon exists in Firestore and validates usage
   */
  async validateCouponInDatabase(couponCode: string, userId: string): Promise<ValidationResult> {
    try {
      const cleanCode = cleanCouponCode(couponCode);

      // Use Cloud Function for secure validation
      const validateCouponFunction = httpsCallable(functions, 'validateCoupon');
      const result = await validateCouponFunction({ couponCode: cleanCode });

      return result.data as ValidationResult;
    } catch (error: any) {

      // Handle specific error codes
      if (error.code === 'functions/unauthenticated') {
        return {
          isValid: false,
          message: 'Please log in to validate coupons.',
          errorCode: 'UNAUTHENTICATED'
        };
      }

      if (error.code === 'functions/invalid-argument') {
        return {
          isValid: false,
          message: 'Invalid coupon code format.',
          errorCode: 'INVALID_FORMAT'
        };
      }

      return {
        isValid: false,
        message: 'Failed to validate coupon. Please try again.',
        errorCode: 'VALIDATION_ERROR'
      };
    }
  }

  /**
   * Main validation method
   */
  async validateCoupon(couponCode: string, userId: string, onStep?: (step: ValidationStep) => void): Promise<ValidationResult> {
    // Step 1: Format validation
    onStep?.({ step: '1/3', status: 'pending' });

    const formatResult = this.validateCouponFormat(couponCode);
    if (!formatResult.isValid) {
      onStep?.({ step: '1/3', status: 'failed', message: formatResult.message });
      return formatResult;
    }

    onStep?.({ step: '1/3', status: 'completed', message: 'Format validated' });

    // Step 2: Database lookup
    onStep?.({ step: '2/3', status: 'pending' });

    const dbResult = await this.validateCouponInDatabase(couponCode, userId);
    if (!dbResult.isValid) {
      onStep?.({ step: '2/3', status: 'failed', message: dbResult.message });
      return dbResult;
    }

    onStep?.({ step: '2/3', status: 'completed', message: 'Coupon found in database' });

    // Step 3: Final validation
    onStep?.({ step: '3/3', status: 'completed', message: 'Validation complete' });

    return dbResult;
  }

  /**
   * Process coupon usage using Cloud Function (secure)
   */
  async processCouponUsage(coupon: Coupon, user: User): Promise<ProcessResult> {
    try {
      // Use Cloud Function for secure processing
      const processCouponFunction = httpsCallable(functions, 'processCouponUsage');
      const result = await processCouponFunction({ couponId: coupon.id });

      return result.data as ProcessResult;
    } catch (error: any) {

      // Handle specific error codes
      if (error.code === 'functions/unauthenticated') {
        return {
          success: false,
          message: 'Please log in to use coupons.'
        };
      }

      if (error.code === 'functions/not-found') {
        return {
          success: false,
          message: 'Coupon not found. Please try again.'
        };
      }

      if (error.code === 'functions/already-exists') {
        return {
          success: false,
          message: 'This coupon has already been used.'
        };
      }

      return {
        success: false,
        message: 'Failed to process coupon. Please try again.'
      };
    }
  }
}

export const couponValidator = new CouponValidationService(); 